import { datadogRum } from '@datadog/browser-rum';
import isEmpty from 'lodash/isEmpty';

import { Product, Team } from 'teams';
import { createNameFromAction } from '../../helpers';
import { ActionName, Context } from '../../types';

export const redactContext = (context?: Context) => {
  if (context && window.__redactDataContext__) {
    return {
      redacted: 'Context redacted',
    };
  }
  return context;
};

/**
 * Returns anything set to the RUM global context, such as teams.
 */
export const getGlobalContext = () => datadogRum.getGlobalContext?.() || {};

/**
 * Builds the action name from the saved global context in places in which
 * the parameters cannot easily be passed through (EG: in interceptors)
 */
const buildActionName = (fallbackName: string, actionDetails?: ActionName | string) => {
  if (typeof actionDetails === 'object' && !isEmpty(actionDetails)) {
    return createNameFromAction(actionDetails);
  }

  const context = getGlobalContext();
  const team = context.team as { name: Team; product: Product };
  const name = typeof actionDetails === 'string' ? actionDetails : fallbackName;

  return createNameFromAction({
    teamName: team?.name as Team,
    productName: team?.product as Product,
    name,
  });
};

/**
 * Add Custom DataDog Actions
 */
export const addRUMAction = (actionName?: ActionName | string, context?: Context) => {
  /**
   * Actions can become really hard to manage over time.
   * The following naming convention for action name
   * would make it easier to map an action to a product and team.
   */
  datadogRum.addAction(buildActionName('ACTION', actionName), redactContext(context));
};

/**
 * Add Custom DataDog Errors
 */
export const addRUMError = <ErrorType extends Error>(
  error: ErrorType,
  errorActionName?: ActionName | string,
  /** Any additional attributes to add to the error log */
  context?: Context,
) => {
  datadogRum.addError(error, {
    ...(redactContext(context) || {}),
    errorName: buildActionName(error.name, errorActionName),
  });
};
